import React, { useEffect, useRef, useState } from "react";
import { number, func, string, bool } from "prop-types";

import { useTranslation } from "@lib/i18n";
import { toast } from "@lib/reactToastify";

const NumberInput = ({
  value,
  onChange,
  minimum = 0,
  maximum = 10,
  step = 1,
  maximumErrorMessage,
  minimumErrorMessage,
  disabled,
}) => {
  const [displayedValue, setDisplayedValue] = useState(value);
  const { t } = useTranslation();
  const inputElement = useRef(null);

  const validate = value => {
    if (value < minimum) {
      const errorMessage =
        minimumErrorMessage ?? t("validation.minimum", { minimum });

      throw new Error(errorMessage);
    }

    if (value > maximum) {
      const errorMessage =
        maximumErrorMessage ?? t("validation.maximum", { maximum });

      throw new Error(errorMessage);
    }
  };

  const handleChange = targetValue => {
    if (disabled) {
      return;
    }

    try {
      validate(targetValue);

      inputElement.current.setCustomValidity("");
      onChange(targetValue);
    } catch (error) {
      inputElement.current.setCustomValidity(error.message);
      inputElement.current.reportValidity();
      toast.error(error.message, {}, error);
      setDisplayedValue(value);
    }
  };

  const onMinusClick = () => {
    const targetValue = value - step;

    handleChange(targetValue);
  };

  const handlePlusClick = () => {
    const targetValue = value + step;

    handleChange(targetValue);
  };

  const onInputChange = event => {
    setDisplayedValue(event.target.value);
  };

  const onInputBlur = event => {
    if (!event.target.value) {
      return setDisplayedValue(value);
    }

    handleChange(Number(event.target.value));
  };

  useEffect(() => {
    try {
      validate(value);

      setDisplayedValue(value);
    } catch (error) {
      onChange(value > maximum ? maximum : minimum);
    }
  }, [value, maximum, minimum]);

  return (
    <div className="flex justify-between w-48">
      <button
        className="
          block size-12 rounded-sm text-xl font-semibold text-primaryOrange cursor-pointer
          border-2 border-primaryOrange transition duration-150
          hover:scale-105 active:scale-95
          hover:bg-primaryOrange hover:text-white
          disabled:pointer-events-none disabled:text-greyLight disabled:border-current
          after:content-['-']
        "
        disabled={disabled || value <= minimum}
        onClick={onMinusClick}
      />
      <input
        className="block w-20 h-12 rounded-sm border-2 border-greyLight font-semibold text-lg text-center appearance-textfield"
        disabled={disabled}
        onChange={onInputChange}
        onBlur={onInputBlur}
        ref={inputElement}
        type="number"
        value={displayedValue}
      />
      <button
        className="
          block size-12 rounded-sm text-xl font-semibold text-primaryOrange cursor-pointer
          border-2 border-primaryOrange transition duration-150
          hover:scale-105 active:scale-95
          hover:bg-primaryOrange hover:text-white
          disabled:pointer-events-none disabled:text-greyLight disabled:border-current
          after:content-['+']
        "
        disabled={disabled || value >= maximum}
        onClick={handlePlusClick}
      />
    </div>
  );
};

NumberInput.propTypes = {
  value: number.isRequired,
  onChange: func.isRequired,
  maximum: number,
  maximumErrorMessage: string,
  minimum: number,
  minimumErrorMessage: string,
  step: number,
  disabled: bool,
};

export default NumberInput;
